import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import PowerPlantOfferMaker from './components/PowerPlantOfferMaker';
import { Save, FolderOpen, Check, AlertCircle, FileText, Calendar, DollarSign, X } from 'lucide-react';
import { devLog } from './utils/logger';

const PowerPlantOfferEnhanced = () => {
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [savedOffers, setSavedOffers] = useState([]);
  const [showSavedOffers, setShowSavedOffers] = useState(false);
  const [currentOfferId, setCurrentOfferId] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [loadedOfferData, setLoadedOfferData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoadingOffer, setIsLoadingOffer] = useState(false);

  // Memoized callback to prevent infinite loops
  const handleDataChange = useCallback((data) => {
    setOfferData(data);
  }, []);

  // --- Image optimization & storage helpers ---
  const dataUrlToImage = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

  const getMimeFromDataUrl = (dataUrl) => {
    const match = /^data:([^;]+);/i.exec(dataUrl || '');
    return (match && match[1]) || 'image/jpeg';
  };

  const getArrayBufferFromDataUrl = async (dataUrl) => {
    const res = await fetch(dataUrl);
    return await res.arrayBuffer();
  };

  // Removed EXIF orientation reader - NO ROTATION

  const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

  const optimizeDataUrl = async (dataUrl, options = {}) => {
    const { maxWidth = 3840, maxHeight = 3840, jpegQuality = 0.95 } = options;
    try {
      const sourceMime = getMimeFromDataUrl(dataUrl);
      const isPng = /png$/i.test(sourceMime);
      const img = await dataUrlToImage(dataUrl);

      // Only downscale if larger than limits
      const needsResize = (img.width || 0) > maxWidth || (img.height || 0) > maxHeight;
      const ratio = needsResize ? Math.min(maxWidth / (img.width || 1), maxHeight / (img.height || 1), 1) : 1;
      const targetWidth = Math.round((img.width || 1) * ratio);
      const targetHeight = Math.round((img.height || 1) * ratio);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // NO ROTATION - draw images exactly as they are
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Preserve original format: PNG stays PNG; JPEG stays JPEG
      const outType = isPng ? 'image/png' : 'image/jpeg';
      const quality = isPng ? undefined : jpegQuality;
      let blob = await canvasToBlob(canvas, outType, quality);

      // Fallback to original data
      if (!blob) {
        const res = await fetch(dataUrl);
        const origBlob = await res.blob();
        return { blob: origBlob, contentType: origBlob.type || outType };
      }

      return { blob, contentType: outType };
    } catch (e) {
      try {
        const res = await fetch(dataUrl);
        const origBlob = await res.blob();
        return { blob: origBlob, contentType: origBlob.type || 'application/octet-stream' };
      } catch (e2) {
        return { blob: null, contentType: 'application/octet-stream' };
      }
    }
  };

  const uploadBlobToStorage = async ({ blob, path, contentType }) => {
    const bucket = 'offer-images';
    const { data, error } = await supabase.storage.from(bucket).upload(path, blob, {
      contentType: contentType || 'application/octet-stream',
      upsert: true
    });
    if (error) {
      console.error('Storage upload error:', error);
      throw error;
    }
    // Return public URL for stability with thumbnails and PDF
    const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
    return pub?.publicUrl || null;
  };

  const limitConcurrency = async (items, limit, worker) => {
    const results = [];
    let idx = 0;
    const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
      while (idx < items.length) {
        const current = idx++;
        results[current] = await worker(items[current], current);
      }
    });
    await Promise.all(runners);
    return results;
  };

  // Prepare offer data for saving: optimize images and move to storage
  const prepareOfferDataForSave = async (rawOfferData, userId, quotationNo, strictStorage = true) => {
    if (!rawOfferData) return rawOfferData;

    const timestamp = Date.now();
    const baseFolder = `power-plant/${userId}/${quotationNo || 'draft'}-${timestamp}`;

    const clone = JSON.parse(JSON.stringify(rawOfferData));

    // Helper to process one data URL
    const processDataUrl = async (dataUrl, fileNamePrefix) => {
      const { blob, contentType } = await optimizeDataUrl(dataUrl);
      if (!blob) return dataUrl; // fallback to original if we cannot get blob
      const ext = contentType === 'image/png' ? 'png' : 'jpg';
      const path = `${baseFolder}/${fileNamePrefix}-${Math.random().toString(36).slice(2)}.${ext}`;
      try {
        const url = await uploadBlobToStorage({ blob, path, contentType });
        return url || dataUrl; // prefer URL, fallback to dataUrl
      } catch (err) {
        if (strictStorage) {
          throw err;
        }
        return dataUrl; // storage failed, fallback only if not strict
      }
    };

    // Images array
    if (Array.isArray(clone.images) && clone.images.length > 0) {
      const images = clone.images;
      const processed = await limitConcurrency(images, 3, async (img, index) => {
        if (!img?.url || /^https?:\/\//i.test(img.url)) return img; // already a URL
        if (!img.url.startsWith('data:image')) return img; // not an inline image
        const url = await processDataUrl(img.url, `image-${index + 1}`);
        return { ...img, url };
      });
      clone.images = processed;
    }

    // Logo
    if (clone.logoImage && typeof clone.logoImage === 'string' && clone.logoImage.startsWith('data:image')) {
      clone.logoImage = await processDataUrl(clone.logoImage, 'logo');
    }

    // Watermark
    if (clone.watermarkImage && typeof clone.watermarkImage === 'string' && clone.watermarkImage.startsWith('data:image')) {
      clone.watermarkImage = await processDataUrl(clone.watermarkImage, 'watermark');
    }

    return clone;
  };

  // Load saved power plant offers
  const loadSavedOffers = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    devLog('PowerPlant User:', user);
    setCurrentUser(user); // Store current user info for display

    // Get user profile to check role from database
    let role = 'employee'; // Default fallback
    
    try {
      const { data: userProfile, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('id', user.id)
        .single();

      devLog('PowerPlant User profile query result:', userProfile);
      devLog('PowerPlant User profile query error:', roleError);
      
      if (!roleError && userProfile?.role) {
        // Check if role is admin (case-insensitive)
        const dbRole = String(userProfile.role).trim().toLowerCase();
        if (dbRole === 'admin') {
          role = 'admin';
          devLog('PowerPlant: User has admin role in database');
        }
      }
    } catch (error) {
      devLog('PowerPlant Role query failed, checking fallback:', error);
    }
    
    // If database check didn't work, check if user is in the hardcoded admin list
    if (role !== 'admin') {
      const userEmail = user.email?.toLowerCase() || '';
      const allowedAdmins = [
        'kerem@atlantismarinepower.com',
        'alp@fin-marine.com'
      ];
      
      if (allowedAdmins.includes(userEmail)) {
        role = 'admin';
        devLog('PowerPlant: User identified as admin from allowed admin list');
      }
      
      devLog('PowerPlant - Checking email:', userEmail);
      devLog('PowerPlant - Allowed admins:', allowedAdmins);
    }
    
    devLog('PowerPlant Final user role determined:', role);
    setUserRole(role);

    // Build query - only fetch lightweight metadata (NOT the heavy offer_data)
    // We'll fetch offer_data only when user clicks "Load" on a specific offer
    let query = supabase
      .from('offers')
      .select('id, offer_number, customer_name, total_amount, currency, status, created_at, updated_at, created_by, offer_data->plantTitle, offer_data->plantCapacity')
      .eq('type', 'power_plant')
      .eq('status', 'draft')
      .order('created_at', { ascending: false })
      .limit(100);

    // Only filter by user_id if NOT admin (but ensure we always show user's own offers)
    if (role !== 'admin') {
      devLog('Filtering by user ID for employee role');
      query = query.eq('created_by', user.id);
    } else {
      devLog('Admin role: fetching all power plant offers');
    }

    const { data, error } = await query;

    devLog('PowerPlant Offers fetched:', data);
    devLog('PowerPlant Query error:', error);

    if (error) {
      console.error('Error loading power plant offers:', error);
      return;
    }

    if (data) {
      setSavedOffers(data);
    }
  };

  // Save power plant offer
  const saveOffer = async () => {
    devLog('PowerPlant Save - offerData received:', offerData);
    
    if (!offerData) {
      alert('No offer data to save. Please fill in the form first.');
      return;
    }
    
    setSaveStatus('saving');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSaveStatus('error');
        alert('You must be logged in to save offers.');
        return;
      }

      // Calculate total amount from power plant data
      const totalAmount = parseFloat(offerData.price?.replace(/[^\d.]/g, '') || '0') || 0;

      // Create offer object matching the database structure (same as Marine)
      const baseOffer = {
        type: 'power_plant',
        offer_number: offerData.quotationNo || `PP-${Date.now()}`,
        customer_name: offerData.clientName || 'Unknown Customer',
        offer_data: {},
        total_amount: totalAmount,
        currency: 'USD', // Power plant default currency
        status: 'draft',
        created_by: user.id
      };

      // Optimize and externalize images before save to avoid huge payloads and timeouts
      const optimizedOfferData = await prepareOfferDataForSave(offerData, user.id, baseOffer.offer_number, true);

      const offerToSave = { ...baseOffer, offer_data: optimizedOfferData };

      devLog('PowerPlant Save - Saving offer:', offerToSave);
      
      // Check if an offer with the same quotation number already exists.
      // maybeSingle() returns null (no 406/PGRST116 error) when no rows match,
      // unlike single(), which logged a red 406 in the console on every new offer.
      const { data: existingOffers, error: checkError } = await supabase
        .from('offers')
        .select('id')
        .eq('type', 'power_plant')
        .eq('created_by', user.id)
        .eq('offer_number', offerToSave.offer_number)
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      let result;
      
      if (existingOffers) {
        // Update existing offer with the same offer number
        devLog('PowerPlant Save - Updating existing offer with ID:', existingOffers.id);
        result = await supabase
          .from('offers')
          .update(offerToSave)
          .eq('id', existingOffers.id);
      } else {
        // Create new offer
        devLog('PowerPlant Save - Creating new offer');
        result = await supabase
          .from('offers')
          .insert([offerToSave]);
      }

      if (result.error) {
        console.error('Save error:', result.error);
        setSaveStatus('error');
        alert(`Save failed: ${result.error.message || 'Unknown error'}`);
      } else {
        setSaveStatus('saved');
        alert('Successfully saved power plant offer!');
        loadSavedOffers(); // Refresh the list
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
      if (String(error?.message || '').toLowerCase().includes('bucket')) {
        alert('Image upload failed: Please create a public Supabase Storage bucket named "offer-images" and allow authenticated users to insert/select. After creating, try again.');
      } else {
        alert(`Save failed: ${error.message || 'Unknown error'}`);
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };



  // Load selected power plant offer - fetch full data only when needed
  const loadOffer = async (offer) => {
    try {
      // Show loading state
      setIsLoadingOffer(true);
      setShowSavedOffers(false);
      
      // Fetch full offer data including the heavy JSON
      const { data, error } = await supabase
        .from('offers')
        .select('offer_data')
        .eq('id', offer.id)
        .single();
      
      if (error) throw error;
      
      let fullData = data?.offer_data || null;

      // Fast URL normalizer - no API calls needed for public URLs
      const normalizeUrl = (url) => {
        if (!url || typeof url !== 'string') return url;
        // If it's a data URL, leave it as-is
        if (url.startsWith('data:image')) return url;
        // If it's already a public URL, return as-is
        if (url.includes('/storage/v1/object/public/')) return url;
        
        // If it's a signed URL, convert to public URL (fast, no API call)
        const m = url.match(/\/storage\/v1\/object\/sign\/([^/]+)\/(.*?)(?:\?|$)/);
        if (m && m[1] && m[2]) {
          const bucket = m[1];
          const path = decodeURIComponent(m[2]);
          // Construct public URL directly - no API call needed!
          const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
          return publicUrl;
        }
        
        return url;
      };

      if (fullData) {
        // Normalize images array - synchronous, no await needed
        if (Array.isArray(fullData.images)) {
          fullData.images = fullData.images.map(img => ({
            ...img,
            url: normalizeUrl(img?.url)
          }));
        }
        // Logo & watermark
        if (fullData.logoImage) fullData.logoImage = normalizeUrl(fullData.logoImage);
        if (fullData.watermarkImage) fullData.watermarkImage = normalizeUrl(fullData.watermarkImage);
      }
      setLoadedOfferData(fullData);
      setCurrentOfferId('loaded-' + offer.id);
      setOfferData(fullData);
      setIsLoadingOffer(false);
    } catch (e) {
      console.error('Failed to load full offer data:', e);
      setIsLoadingOffer(false);
      alert('Failed to load offer. Please try again.');
      setShowSavedOffers(true);
    }
  };

  // Delete power plant offer
  const deleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this power plant offer?')) return;
    
    const { error } = await supabase
      .from('offers')
      .delete()
      .eq('id', offerId)
      .eq('type', 'power_plant'); // Extra safety check

    if (!error) {
      loadSavedOffers(); // Refresh the list
      alert('Power plant offer deleted successfully!');
    } else {
      alert('Error deleting offer. Please try again.');
    }
  };

  // Filter offers based on search term
  const filteredOffers = savedOffers.filter(offer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      offer.customer_name?.toLowerCase().includes(search) ||
      offer.offer_number?.toLowerCase().includes(search) ||
      offer.plantTitle?.toLowerCase().includes(search) ||
      offer.plantCapacity?.toLowerCase().includes(search) ||
      new Date(offer.created_at).toLocaleDateString().includes(search)
    );
  });

  useEffect(() => {
    loadSavedOffers();
  }, []);

  return (
    <div className="power-plant-offer-enhanced">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: 0, fontSize: 'clamp(20px, 5.2vw, 28px)', fontWeight: 'bold' }}>
          ⚡ Power Plant Offer Builder
        </h1>
        <p style={{ margin: '8px 0 0 0', fontSize: '16px', opacity: 0.9 }}>
          Create professional offers for power plant equipment
        </p>
      </div>

      {/* Save/Load Bar */}
      <div className="top-buttons-container" style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Save Draft Button */}
        <button
          onClick={saveOffer}
          disabled={!offerData || saveStatus === 'saving'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: saveStatus === 'saved' ? '#4CAF50' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
            fontSize: 'clamp(12px, 3vw, 14px)',
            fontWeight: '500',
            opacity: (!offerData || saveStatus === 'saving') ? 0.6 : 1,
            flex: '0 1 auto',
            whiteSpace: 'nowrap'
          }}
        >
          {saveStatus === 'saving' ? (
            <>Loading...</>
          ) : saveStatus === 'saved' ? (
            <>
              <Check size={18} />
              Saved!
            </>
          ) : saveStatus === 'error' ? (
            <>
              <AlertCircle size={18} />
              Error
            </>
          ) : (
            <>
              <Save size={18} />
              Save Draft
            </>
          )}
        </button>

        {/* My Offers Button - refresh when clicked */}
        <button
          onClick={() => {
            loadSavedOffers();
            setShowSavedOffers(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: 'clamp(12px, 3vw, 14px)',
            fontWeight: '500',
            flex: '0 1 auto',
            whiteSpace: 'nowrap'
          }}
        >
          <FolderOpen size={16} />
          <span style={{ 
            display: 'inline',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {userRole === 'admin' ? `All Offers (${savedOffers.length})` : `My Offers (${savedOffers.length})`}
          </span>
        </button>
      </div>

      {/* Saved Offers Modal */}
      {showSavedOffers && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '10px'
        }}>
          <div className="modal-class smooth-scroll" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '15px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '15px',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <h2 style={{ 
                margin: 0,
                fontSize: 'clamp(18px, 4vw, 24px)'
              }}>{userRole === 'admin' ? 'All Power Plant Offers' : 'My Power Plant Offers'}</h2>
              <button
                onClick={() => setShowSavedOffers(false)}
                className="modal-close"
                style={{
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Search Bar */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="max-lg:hidden"
                  placeholder="Search power plant offers by customer, quote number, plant title, or date..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  className="lg:hidden"
                  placeholder="Search offers"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#999'
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                Showing {filteredOffers.length} of {savedOffers.length} power plant offers
              </div>
            </div>

            {/* Offers List */}
            <div className="smooth-scroll pp-offers-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {filteredOffers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  {savedOffers.length === 0 ? 'No power plant offers saved yet.' : 'No power plant offers match your search.'}
                </div>
              ) : (
                filteredOffers.map((offer) => (
                  <div
                    key={offer.id}
                    className="offer-card"
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '10px',
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    {/* Header with customer name */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '10px',
                      flexWrap: 'wrap'
                    }}>
                      <FileText size={16} color="#FF9800" />
                      <h4 style={{ 
                        margin: 0, 
                        color: '#333',
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        wordBreak: 'break-word'
                      }}>{offer.customer_name}</h4>
                    </div>
                    
                    {/* Show plant title if available */}
                    {offer.plantTitle && (
                      <div style={{ 
                        fontSize: 'clamp(13px, 3vw, 15px)', 
                        fontWeight: '500', 
                        color: '#2196F3', 
                        marginBottom: '10px',
                        backgroundColor: '#E3F2FD',
                        padding: '8px 10px',
                        borderRadius: '4px',
                        border: '1px solid #BBDEFB',
                        wordBreak: 'break-word'
                      }}>
                        🏭 {offer.plantTitle}{offer.plantCapacity ? ` - ${offer.plantCapacity}` : ''}
                      </div>
                    )}
                    
                    {/* Offer details - stack on mobile */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                      gap: '8px', 
                      marginBottom: '10px',
                      fontSize: 'clamp(12px, 2.5vw, 13px)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        <FileText size={12} color="#666" />
                        <span style={{ color: '#666', wordBreak: 'break-all' }}>
                          <strong>Quote:</strong> {offer.offer_number}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        <Calendar size={12} color="#666" />
                        <span style={{ color: '#666' }}>
                          {new Date(offer.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                         <DollarSign size={12} color="#666" />
                         <span style={{ color: '#666', fontWeight: '500' }}>
                           {offer.total_amount ? `$${offer.total_amount.toLocaleString()}` : 'TBD'}
                         </span>
                       </div>
                    </div>
                    
                    {/* Status info - more compact on mobile */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                      gap: '6px', 
                      marginBottom: '12px',
                      padding: '6px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      fontSize: 'clamp(11px, 2.5vw, 12px)',
                      color: '#555'
                    }}>
                      <div style={{ wordBreak: 'break-word' }}>
                        <strong>📊 Status:</strong> {offer.status?.toUpperCase() || 'DRAFT'}
                      </div>
                    </div>
                    
                    {/* Action buttons - stack on mobile */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => loadOffer(offer)}
                        style={{
                          flex: '1 1 auto',
                          minWidth: '80px',
                          padding: '8px 12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: 'clamp(12px, 3vw, 14px)',
                          fontWeight: '500'
                        }}
                      >
                        Load
                      </button>
                      <button
                        onClick={() => deleteOffer(offer.id)}
                        style={{
                          flex: '1 1 auto',
                          minWidth: '80px',
                          padding: '8px 12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: 'clamp(12px, 3vw, 14px)',
                          fontWeight: '500'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoadingOffer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 50px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f4f6',
              borderTop: '5px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ 
              margin: 0, 
              fontSize: '16px',
              fontWeight: '500',
              color: '#333'
            }}>Loading offer...</p>
          </div>
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* ---- Phone / small-tablet only (desktop >= 1024px is untouched) ---- */
        @media (max-width: 1023px) {
          /* Sit above the app header and the fixed bottom bar, and keep taps. */
          .power-plant-offer-enhanced .modal-overlay {
            z-index: 1000 !important;
            pointer-events: auto !important;
            align-items: flex-start !important;
            padding-top: 4.75rem !important;
            box-sizing: border-box;
          }
          .power-plant-offer-enhanced .modal-class {
            position: relative !important;
            inset: auto !important;
            margin-top: env(safe-area-inset-top, 0px) !important;
            height: auto !important;
            max-height: calc(100dvh - env(safe-area-inset-top, 0px) - 4.75rem - 1rem - env(safe-area-inset-bottom, 0px)) !important;
            pointer-events: auto;
          }
          .power-plant-offer-enhanced .modal-close {
            position: relative !important;
            top: auto !important;
            right: auto !important;
            width: 44px !important;
            height: 44px !important;
            min-width: 44px;
            min-height: 44px;
            flex-shrink: 0;
            z-index: 2;
            pointer-events: auto;
          }
          /* The modal itself scrolls - no scroll box inside a scroll box */
          .power-plant-offer-enhanced .pp-offers-list {
            max-height: none !important;
            overflow-y: visible !important;
          }
          .power-plant-offer-enhanced .offer-card button {
            min-height: 44px;
            font-size: 16px;
          }
          .power-plant-offer-enhanced .modal-class input,
          .power-plant-offer-enhanced .modal-class select,
          .power-plant-offer-enhanced .modal-class button {
            font-size: 16px;
            min-height: 44px;
          }
          .power-plant-offer-enhanced .top-buttons-container button {
            font-size: 16px;
          }
        }

        /* Two-row app header (below 768px) is about 114px. Clear it, plus the notch. */
        @media (max-width: 767px) {
          .power-plant-offer-enhanced .modal-overlay {
            padding-top: 8rem !important;
          }
          .power-plant-offer-enhanced .modal-class {
            max-height: calc(100dvh - env(safe-area-inset-top, 0px) - 8rem - 1rem - env(safe-area-inset-bottom, 0px)) !important;
          }
        }
      `}</style>

      {/* Power Plant Offer Maker with data binding */}
      <PowerPlantOfferMaker 
        key={currentOfferId || 'new'}
        initialData={loadedOfferData || offerData}
        onDataChange={handleDataChange}
      />
    </div>
  );
};

export default PowerPlantOfferEnhanced; 